import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  Component,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";

import { Subject } from "rxjs";
import { ManualQuote } from "../models/quote-manual.model";
import { quoteStatus } from "../quote-form/quote-status.const";
import { WysiwygComponent } from "../../ui/wysiwyg/wysiwyg.component";
import { InputText } from "primeng/inputtext";
import { CurrencySelectComponent } from "../../ui/dropdown/currency-select/currency-select.component";

@Component({
  selector: "app-quote-manual-editor",
  templateUrl: "./quote-manual-editor.component.html",
  styleUrls: ["./quote-manual-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    WysiwygComponent,
    FormsModule,
    ReactiveFormsModule,
    InputText,
    CurrencySelectComponent,
  ],
})
export class QuoteManualEditorComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);

  public readonly quote = input<ManualQuote>(undefined);
  public readonly valueChanged = output<{
    manualQuote: { htmlContent?: string; form?: any; currency?: string };
  }>();

  public priceForm: UntypedFormGroup;
  private editorContentSubject = new Subject<string>();
  private tmpNetPriceValue = 0;
  private tmpMarginValue = 0;
  private tmpTotalValue = 0;

  ngOnInit() {
    this.priceForm = this.createForm();

    this.editorContentSubject
      .pipe(distinctUntilChanged())
      .subscribe((content) => {
        this.quote().setHtmlContent(content);

        if (this.quote().getStatus() === quoteStatus.draft) {
          this.valueChanged.emit({ manualQuote: { htmlContent: content } });
        }
      });

    this.priceForm.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(500))
      .subscribe((formChanges) => {
        this.quote().setNetPrice(formChanges.netPrice);
        this.quote().setMargin(formChanges.total - formChanges.netPrice);

        this.priceForm.controls.total.setValue(
          this.quote().getNetPrice() + this.quote().getMargin(),
        );

        if (
          this.tmpNetPriceValue !== this.priceForm.controls.netPrice.value ||
          this.tmpMarginValue !== this.priceForm.controls.margin.value
        ) {
          this.priceForm.controls.total.setValue(
            this.priceForm.controls.netPrice.value +
              this.priceForm.controls.margin.value,
          );
        }

        if (this.tmpTotalValue !== this.priceForm.controls.total.value) {
          this.priceForm.controls.margin.setValue(
            this.priceForm.controls.total.value -
              this.priceForm.controls.netPrice.value,
          );
        }

        this.tmpNetPriceValue = this.priceForm.controls.netPrice.value;
        this.tmpMarginValue = this.priceForm.controls.margin.value;
        this.tmpTotalValue = this.priceForm.controls.total.value;

        if (
          this.tmpNetPriceValue !== formChanges.netPrice ||
          this.tmpMarginValue !== formChanges.margin ||
          this.tmpTotalValue !== formChanges.total
        ) {
          this.valueChanged.emit({
            manualQuote: { form: this.priceForm.value },
          });
        }
      });
  }

  public contentChanged(event: string) {
    this.editorContentSubject.next(event);
  }

  public currencyChanged(currency: string) {
    this.quote().setCurrency(currency);
    this.valueChanged.emit({ manualQuote: { currency } });
  }

  public createForm(): UntypedFormGroup {
    const quote = this.quote();
    const quoteValue = this.quote();
    const quoteVal = this.quote();
    return this.formBuilder.group({
      netPrice: [
        quote ? quote.getNetPrice() : quote.getNetPrice() || 0,
        Validators.required,
      ],
      margin: [
        quoteValue ? quoteValue.getMargin() : quoteValue.getMargin() || 0,
        Validators.required,
      ],
      total: [
        quoteVal
          ? quoteVal.getNetPrice() && quoteVal.getMargin()
            ? quoteVal.getNetPrice() + quoteVal.getMargin()
            : 0
          : 0,
        Validators.required,
      ],
    });
  }
}
